import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CreditCardIcon } from '@heroicons/react/24/outline';
import {
  getPublicPaymentDetails,
  initiatePublicPaycorpPayment,
  submitPublicManualPayment,
} from '../../../../services/twApi';
import { uploadFilePublic } from '../../../../services/fileApi';
import LoadingSpinner from '../../../Common/LoadingSpinner';
import ProofAttachmentDropzone from '../../../Common/ProofAttachmentDropzone';
import { getPublicPaymentSummaryRows } from './twPublicPaymentHelpers';
import { formatTWCurrency, formatTWDateTime } from '../Common/twListHelpers';
import TWBankAccountInfoCard from '../Common/TWBankAccountInfoCard';
import toast from 'react-hot-toast';
import { toPaymentDateTimeIso } from '../../../../utils/crmPaymentFinanceDefaults';

const PAYMENT_METHODS = [
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'online_payment', label: 'Online Payment' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'mobile_payment', label: 'Mobile Payment' },
  { value: 'other', label: 'Other' },
];

const methodShowsBankFields = (method) => ['bank_transfer', 'cheque'].includes(method);

export default function PublicTWPayment() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('choose');
  const [submitting, setSubmitting] = useState(false);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [done, setDone] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);

  const [form, setForm] = useState({
    amount: '',
    paymentMethod: 'bank_transfer',
    paymentDate: new Date().toISOString().split('T')[0],
    referenceNumber: '',
    chequeNumber: '',
    bankName: '',
    notes: '',
  });

  useEffect(() => {
    getPublicPaymentDetails(token)
      .then((res) => {
        const d = res.data?.data;
        setData(d);
        const payable = d?.invoice?.availableAmount ?? d?.invoice?.balanceAmount;
        const invoiceNumber = d?.invoice?.invoiceNumber || '';
        setForm((f) => ({
          ...f,
          ...(payable ? { amount: Number(payable).toFixed(2) } : {}),
          referenceNumber: invoiceNumber,
        }));
        if (d?.alreadyPaid) setStep('paid');
      })
      .catch((err) => setError(err.response?.data?.message || 'Invalid or expired payment link.'))
      .finally(() => setLoading(false));
  }, [token]);

  const inv = data?.invoice;
  const bankAccountInfo = data?.bankAccountInfo;
  const summaryRows = getPublicPaymentSummaryRows(data);
  const currency = inv?.currency || 'LKR';
  // Amount the participant can pay right now = balance minus anything already awaiting approval.
  const payableAmount = Math.round(Number(inv?.availableAmount ?? inv?.balanceAmount ?? 0) * 100) / 100;
  const pendingAmount = Number(inv?.pendingAmount ?? 0);
  const lastRejection = data?.lastRejection;

  const handleCardPay = async () => {
    const amount = Number(form.amount);
    if (!amount || amount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    if (amount > payableAmount + 0.01) {
      toast.error(`Amount cannot exceed ${formatTWCurrency(payableAmount, currency)} available to pay right now`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await initiatePublicPaycorpPayment(token, { amount });
      const url = res.data?.data?.paymentUrl || res.data?.data?.paymentPageUrl;
      if (!url) throw new Error('Payment gateway did not return a payment page URL');
      window.location.href = url;
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    const amount = Number(form.amount);
    if (!amount || amount <= 0) {
      toast.error('Enter a valid payment amount');
      return;
    }
    if (amount > payableAmount + 0.01) {
      toast.error(
        pendingAmount > 0
          ? `You can pay up to ${formatTWCurrency(payableAmount, currency)} now — ${formatTWCurrency(pendingAmount, currency)} is already awaiting approval.`
          : `Amount cannot exceed balance due (${formatTWCurrency(payableAmount, currency)})`
      );
      return;
    }
    if (methodShowsBankFields(form.paymentMethod) && !receiptFile) {
      toast.error('Payment proof (bank slip / cheque image) is required for bank transfer and cheque payments');
      return;
    }
    if (form.paymentMethod === 'cheque' && !String(form.chequeNumber || '').trim()) {
      toast.error('Please enter the cheque number');
      return;
    }
    if (!termsAgreed) {
      toast.error('Please agree to the payment terms before submitting');
      return;
    }

    setSubmitting(true);
    try {
      let receiptDocument;
      if (receiptFile) {
        setUploadingReceipt(true);
        const uploadRes = await uploadFilePublic(receiptFile, {
          category: 'Payment Receipt',
          description: `T&W workshop payment — ${form.paymentMethod}`,
          title: receiptFile.name,
        });
        const uploaded = uploadRes.data?.data || uploadRes.data;
        setUploadingReceipt(false);
        receiptDocument = {
          fileId: uploaded.fileId,
          fileName: uploaded.fileName || receiptFile.name,
          originalName: uploaded.originalName || receiptFile.name,
          fileSize: uploaded.fileSize || receiptFile.size,
          mimeType: uploaded.mimeType || receiptFile.type,
        };
      }

      await submitPublicManualPayment(token, {
        amount,
        paymentMethod: form.paymentMethod,
        paymentDate: toPaymentDateTimeIso(form.paymentDate),
        referenceNumber: inv?.invoiceNumber || form.referenceNumber,
        chequeNumber: form.paymentMethod === 'cheque' ? form.chequeNumber : undefined,
        bankName: form.bankName,
        notes: form.notes,
        ...(receiptDocument ? { receiptDocument } : {}),
      });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setSubmitting(false);
      setUploadingReceipt(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center p-8 max-w-md">
          <p className="text-red-500 font-medium">{error}</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-xl border border-gray-200 p-8 max-w-md w-full text-center shadow-sm">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Payment Submitted</h2>
          <p className="text-sm text-gray-500">
            Your payment and proof document have been submitted for verification.
            A receipt will be issued after approval.
          </p>
        </div>
      </div>
    );
  }

  if (step === 'paid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-xl border border-gray-200 p-8 max-w-md w-full text-center shadow-sm">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Already Paid</h2>
          <p className="text-sm text-gray-500">This invoice has already been fully paid. Thank you.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Workshop Payment</h1>
          <p className="text-sm text-gray-500 mt-1">Invoice {inv?.invoiceNumber}</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4 shadow-sm">
          <div className="space-y-3 text-sm">
            {summaryRows.map(([l, v]) => (
              <div key={l} className="flex justify-between gap-4">
                <span className="text-gray-500">{l}</span>
                <span className="font-medium text-gray-900 text-right">{v}</span>
              </div>
            ))}
          </div>
        </div>

        {lastRejection && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
            <p className="text-sm font-semibold text-red-800">Your previous payment was not accepted</p>
            <p className="text-xs text-red-700 mt-1">
              The payment{lastRejection.amount ? ` of ${formatTWCurrency(lastRejection.amount, lastRejection.currency)}` : ''}
              {lastRejection.rejectedAt ? ` was rejected on ${formatTWDateTime(lastRejection.rejectedAt)}` : ' was rejected'}.
            </p>
            {lastRejection.reason && (
              <p className="text-xs text-red-700 mt-1">
                <span className="font-medium">Reason:</span> {lastRejection.reason}
              </p>
            )}
            <p className="text-xs text-red-700 mt-1">Please review the details and submit your payment again below.</p>
          </div>
        )}

        {step === 'choose' && (
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => setStep('card')}
              className="w-full rounded-xl bg-indigo-600 py-3.5 text-sm font-semibold text-white hover:bg-indigo-700 flex items-center justify-center gap-2"
            >
              <CreditCardIcon className="w-5 h-5" />
              Pay by Card (Online)
            </button>
            <button
              type="button"
              onClick={() => setStep('manual')}
              className="w-full rounded-xl border border-gray-300 bg-white py-3.5 text-sm font-semibold text-gray-800 hover:bg-gray-50"
            >
              Bank Transfer / Cheque / Mobile / Other
            </button>
            <p className="text-xs text-center text-gray-500 pt-1">
              Cash payments are not accepted through this link. Upload proof for manual methods; staff will review and approve it before issuing a receipt.
            </p>
          </div>
        )}

        {step === 'card' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-4">
            <h3 className="font-semibold text-gray-900">Card Payment</h3>
            <p className="text-xs text-gray-500">
              You will be redirected to the secure payment gateway. Card payments are confirmed automatically on success.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount (Rs. / LKR)</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            {pendingAmount > 0 && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                {formatTWCurrency(pendingAmount, currency)} is already submitted and awaiting approval.
                You can pay up to {formatTWCurrency(payableAmount, currency)} now.
              </p>
            )}
            <button
              type="button"
              onClick={handleCardPay}
              disabled={submitting}
              className="w-full rounded-xl bg-indigo-600 py-3 text-sm font-semibold text-white disabled:opacity-60"
            >
              {submitting ? 'Redirecting...' : 'Proceed to Payment Gateway'}
            </button>
            <button type="button" onClick={() => setStep('choose')} className="w-full text-sm text-gray-500 hover:underline">
              Back
            </button>
          </div>
        )}

        {step === 'manual' && (
          <form onSubmit={handleManualSubmit} noValidate className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-4">
            <h3 className="font-semibold text-gray-900">Submit Payment Proof</h3>

            {methodShowsBankFields(form.paymentMethod) && (
              <TWBankAccountInfoCard
                bankAccountInfo={bankAccountInfo}
                title="Transfer Funds To"
              />
            )}
            {methodShowsBankFields(form.paymentMethod) && !bankAccountInfo?.accountNumber && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Institution bank details are not configured yet. Please contact SLIM for the account to transfer to before submitting your slip.
              </p>
            )}

            {pendingAmount > 0 && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                {formatTWCurrency(pendingAmount, currency)} is already submitted and awaiting approval.
                You can pay up to {formatTWCurrency(payableAmount, currency)} now; the rest unlocks once that is approved or rejected.
              </p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Amount (Rs. / LKR) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={form.amount}
                  onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                  required
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Payment Date *</label>
                <input
                  type="date"
                  value={form.paymentDate}
                  onChange={(e) => setForm((f) => ({ ...f, paymentDate: e.target.value }))}
                  required
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Payment Method *</label>
              <select
                value={form.paymentMethod}
                onChange={(e) => setForm((f) => ({ ...f, paymentMethod: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                {PAYMENT_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Reference Number</label>
                <input
                  value={inv?.invoiceNumber || form.referenceNumber || ''}
                  readOnly
                  aria-readonly="true"
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 font-mono cursor-default"
                />
                <p className="mt-1 text-[11px] text-gray-500">Uses the invoice number as the payment reference.</p>
              </div>
              {methodShowsBankFields(form.paymentMethod) && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Bank Name</label>
                  <input
                    value={form.bankName}
                    onChange={(e) => setForm((f) => ({ ...f, bankName: e.target.value }))}
                    placeholder="Your bank"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              )}
            </div>

            {form.paymentMethod === 'cheque' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Cheque Number *</label>
                <input
                  value={form.chequeNumber}
                  onChange={(e) => setForm((f) => ({ ...f, chequeNumber: e.target.value }))}
                  placeholder="Enter cheque number"
                  required
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                rows={2}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                placeholder="Optional additional details"
              />
            </div>

            <ProofAttachmentDropzone
              file={receiptFile}
              onChange={setReceiptFile}
              required={methodShowsBankFields(form.paymentMethod)}
              hint={methodShowsBankFields(form.paymentMethod) ? null : '(optional)'}
            />

            <div className={`border rounded-lg p-3 text-xs ${termsAgreed ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={termsAgreed}
                  onChange={(e) => setTermsAgreed(e.target.checked)}
                  className="mt-0.5"
                />
                <span className="text-gray-700">
                  I confirm the payment details and uploaded proof are accurate. I understand verification is required before a receipt is issued.
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={submitting || uploadingReceipt}
              className="w-full rounded-xl bg-green-600 py-3 text-sm font-semibold text-white disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {(submitting || uploadingReceipt) && <LoadingSpinner inline size="sm" color="text-white" center={false} />}
              {uploadingReceipt ? 'Uploading proof...' : submitting ? 'Submitting...' : 'Submit Payment for Verification'}
            </button>
            <button type="button" onClick={() => setStep('choose')} className="w-full text-sm text-gray-500 hover:underline">
              Back
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
